import React from 'react';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';
import {
  pmsDefectSeverityField,
  pmsDefectStatusField,
  pmsEquipmentForVesselField,
  pmsVesselField,
  pmsWorkOrderField} from '../shared/pmsFormFields';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsDefect } from './useData';

function stripDefectPayload(data: Partial<PmsDefect>): Record<string, unknown> {
  const {
    vessel,
    equipment,
    workOrder,
    severity,
    status,
    reportedBy,
    closedBy,
    id,
    createdAt,
    updatedAt,
    reportedAt,
    closedAt,
    reportedById,
    closedById,
    ...payload
  } = data as Record<string, unknown>;
  return payload;
}

export const moduleConfig = {
  module: {
    title: 'Defects',
    permissionModuleKey: 'pms_defects',
    route: {
      list: '/pms/pms-defects',
      new: '/pms/pms-defects/new',
      detail: (id: number) => `/pms/pms-defects/${id}`,
      edit: (id: number) => `/pms/pms-defects/${id}/edit`},
    api: { list: '/pms-defects', detail: (id: number) => `/pms-defects/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsDefect[]) => items ,
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New',
        showTitle: false,
        variant: 'primary' as const,
        href: '/pms/pms-defects/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsDefect) => item.id,
  },
  emptyStates: {
    noData: 'No records.',
    noSearchResults: 'No matches.',
    noFilterResults: 'No matches.',
  },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsDefect } = await import('../../../services/mutations');
      await deletePmsDefect(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: 'Defects',
    getTitle: (item: PmsDefect) =>
      item.defectNumber
        ? `${String(item.defectNumber)}${item.title ? ` — ${String(item.title)}` : ''}`
        : item.title
          ? String(item.title)
          : `#${item.id}`},
  form: {
    new: { title: 'New defect', submitText: 'Create' },
    edit: { title: 'Edit defect', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-5xl',
    create: async (data: Partial<PmsDefect>) => {
      const { createPmsDefect } = await import('../../../services/mutations');
      return createPmsDefect(stripDefectPayload(data));
    },
    update: async (id: number, data: Partial<PmsDefect>) => {
      const { updatePmsDefect } = await import('../../../services/mutations');
      return updatePmsDefect(id, stripDefectPayload(data));
    }},
  fields: [
    {
      ...pmsVesselField(true),
      helperText: 'Vessel where the defect was found',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('equipmentId', '');
        setFieldValue('workOrderId', '');
      }},
    {
      ...pmsEquipmentForVesselField(true),
      helperText: 'Equipment slot affected by this defect',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('workOrderId', '');
      }},
    {
      ...pmsWorkOrderField(false),
      helperText: 'Optional work order that discovered or addresses this defect'},
    {
      name: 'defectNumber',
      label: 'Defect number',
      type: 'text',
      required: true,
      placeholder: 'e.g. DEF-2026-00018',
      validation: { required: true },
      helperText: 'Unique per vessel'},
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Short summary of the defect',
      validation: { required: true }},
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      rows: 4,
      required: true,
      placeholder: 'What was observed, where, and under what conditions',
      validation: { required: true }},
    {
      ...pmsDefectSeverityField(true),
      helperText: 'How serious the defect is for operations / class'},
    {
      ...pmsDefectStatusField(true),
      helperText: 'Current lifecycle status of the defect'},
    {
      name: 'rootCause',
      label: 'Root cause',
      type: 'textarea',
      rows: 2,
      placeholder: 'Underlying cause if known'},
    {
      name: 'temporaryAction',
      label: 'Temporary action',
      type: 'textarea',
      rows: 2,
      placeholder: 'Interim measures to keep operations safe'},
    {
      name: 'correctiveAction',
      label: 'Corrective action',
      type: 'textarea',
      rows: 2,
      placeholder: 'Permanent fix planned or completed'},
    {
      name: 'classNotificationRequired',
      label: 'Class',
      type: 'switch',
      switchLabel: 'Class notification required'},
    {
      name: 'shoreNotificationRequired',
      label: 'Shore',
      type: 'switch',
      switchLabel: 'Shore / office notification required'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'scope',
        title: 'Scope',
        description: 'Where the defect sits and any related work order.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['vesselId', 'equipmentId', 'workOrderId'],
        fieldSpans: {
          vesselId: 'md:col-span-4',
          equipmentId: 'md:col-span-4',
          workOrderId: 'md:col-span-4'},
        collapsible: false},
      {
        id: 'defect',
        title: 'Defect',
        description: 'Identity and description of what was found.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['defectNumber', 'title', 'description'],
        fieldSpans: {
          defectNumber: 'md:col-span-4',
          title: 'md:col-span-8',
          description: 'md:col-span-12'},
        collapsible: false},
      {
        id: 'classification',
        title: 'Classification',
        description: 'Severity and current status.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['severityId', 'statusId'],
        fieldSpans: {
          severityId: 'md:col-span-6',
          statusId: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'analysis',
        title: 'Cause & actions',
        description: 'Root cause analysis and temporary / corrective measures.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['rootCause', 'temporaryAction', 'correctiveAction'],
        fieldSpans: {
          rootCause: 'md:col-span-12',
          temporaryAction: 'md:col-span-6',
          correctiveAction: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'notifications',
        title: 'Notifications',
        description: 'Whether class or shore office must be informed.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['classNotificationRequired', 'shoreNotificationRequired'],
        fieldSpans: {
          classNotificationRequired: 'md:col-span-6',
          shoreNotificationRequired: 'md:col-span-6'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const relName = (
  rel: { code?: string | null; name?: string | null; title?: string | null; vesselName?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  if (rel?.title) return rel.title;
  if (rel?.vesselName) return rel.vesselName;
  return rel?.code ?? (id != null ? `#${id}` : '-');
};

const labelVessel = (item: PmsDefect) => {
  const v = item.vessel as { vesselName?: string; name?: string } | null | undefined;
  return v?.vesselName ?? v?.name ?? (item.vesselId != null ? `#${item.vesselId}` : '-');
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<PmsDefect>[] => [
  {
    key: 'defectNumber',
    header: 'Number',
    sortable: true,
    render: (v) => <TextCell value={v != null ? String(v) : undefined} />},
  { key: 'title', header: 'Title', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'vesselId',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={labelVessel(row)} />},
  {
    key: 'severityId',
    header: 'Severity',
    sortable: true,
    render: (_v, row) => (
      <TextCell
        value={relName(row.severity as { code?: string; name?: string } | null, row.severityId as number | null)}
      />
    )},
  {
    key: 'statusId',
    header: 'Status',
    sortable: true,
    render: (_v, row) => (
      <TextCell
        value={relName(row.status as { code?: string; name?: string } | null, row.statusId as number | null)}
      />
    )},
];

export const getDetailSections = (item: PmsDefect, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Vessel', value: labelVessel(item) },
      {
        label: 'Equipment',
        value: relName(item.equipment as { name?: string } | null, item.equipmentId as number | null)},
      {
        label: 'Work order',
        value: (() => {
          const wo = item.workOrder as
            | { workOrderNumber?: string; title?: string }
            | null
            | undefined;
          if (wo?.workOrderNumber) {
            return wo.title ? `${wo.workOrderNumber} — ${wo.title}` : wo.workOrderNumber;
          }
          return item.workOrderId != null ? `#${item.workOrderId}` : '-';
        })()},
      {
        label: 'Severity',
        value: relName(item.severity as { name?: string; code?: string } | null, item.severityId as number | null)},
      {
        label: 'Status',
        value: relName(item.status as { name?: string; code?: string } | null, item.statusId as number | null)},
      { label: 'Defect number', value: item.defectNumber != null ? String(item.defectNumber) : '-' },
      { label: 'Title', value: item.title != null ? String(item.title) : '-' },
      { label: 'Description', value: item.description != null ? String(item.description) : '-' },
      { label: 'Root cause', value: item.rootCause != null ? String(item.rootCause) : '-' },
      { label: 'Temporary action', value: item.temporaryAction != null ? String(item.temporaryAction) : '-' },
      { label: 'Corrective action', value: item.correctiveAction != null ? String(item.correctiveAction) : '-' },
      { label: 'Class notification', value: item.classNotificationRequired ? 'Yes' : 'No' },
      { label: 'Shore notification', value: item.shoreNotificationRequired ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsDefect> => ({
  vesselId: undefined,
  equipmentId: undefined,
  workOrderId: undefined,
  severityId: undefined,
  statusId: undefined,
  defectNumber: '',
  title: '',
  description: '',
  rootCause: '',
  correctiveAction: '',
  temporaryAction: '',
  classNotificationRequired: false,
  shoreNotificationRequired: false});
export const mapItemToFormData = (item: PmsDefect): Partial<PmsDefect> => ({
  vesselId: item.vesselId != null ? Number(item.vesselId) : undefined,
  equipmentId: item.equipmentId != null ? Number(item.equipmentId) : undefined,
  workOrderId: item.workOrderId != null ? Number(item.workOrderId) : undefined,
  severityId: item.severityId != null ? Number(item.severityId) : undefined,
  statusId: item.statusId != null ? Number(item.statusId) : undefined,
  vessel: item.vessel ?? undefined,
  equipment: item.equipment ?? undefined,
  workOrder: item.workOrder ?? undefined,
  severity: item.severity ?? undefined,
  status: item.status ?? undefined,
  defectNumber: item.defectNumber != null ? String(item.defectNumber) : '',
  title: item.title != null ? String(item.title) : '',
  description: item.description != null ? String(item.description) : '',
  rootCause: item.rootCause != null ? String(item.rootCause) : '',
  correctiveAction: item.correctiveAction != null ? String(item.correctiveAction) : '',
  temporaryAction: item.temporaryAction != null ? String(item.temporaryAction) : '',
  classNotificationRequired: item.classNotificationRequired ?? false,
  shoreNotificationRequired: item.shoreNotificationRequired ?? false});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
